// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * ModulesContext - Manage installed ship modules
 * 
 * Handles purchasing and tracking installed modules.
 * Modules provide passive bonuses and unlock features.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { shipModules } from '../config/ShipModulesConfig';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';

const ModulesContext = createContext(null);

export function ModulesProvider({ children }) {
  // Installed modules: array of module IDs
  const [installedModules, setInstalledModules] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.INSTALLED_MODULES);
    let modules = saved ? JSON.parse(saved) : [];
    
    // ALWAYS ensure cargo bay exists
    if (!modules.includes('cargoBay')) {
      modules.push('cargoBay');
    }
    
    // Save immediately
    localStorage.setItem(STORAGE_KEYS.INSTALLED_MODULES, JSON.stringify(modules));
    
    return modules;
  });

  // Persist to localStorage and ensure cargo bay always exists
  useEffect(() => {
    // Ensure cargo bay is always present
    if (!installedModules.includes('cargoBay')) {
      console.warn('[ModulesContext] Cargo bay missing, re-adding');
      setInstalledModules(prev => [...prev, 'cargoBay']);
      return;
    }
    
    localStorage.setItem(STORAGE_KEYS.INSTALLED_MODULES, JSON.stringify(installedModules));
  }, [installedModules]);

  // Check if module is installed
  const hasModule = useCallback((moduleId) => {
    return installedModules.includes(moduleId);
  }, [installedModules]);

  // Check if can purchase module
  const canPurchaseModule = useCallback((moduleId) => {
    const module = shipModules[moduleId];
    if (!module) return { canPurchase: false, reason: 'Module not found' };
    
    // Check if already installed
    if (hasModule(moduleId)) {
      return { canPurchase: false, reason: 'Already installed' };
    }
    
    // Check requirements
    if (module.requires && !hasModule(module.requires)) {
      return { canPurchase: false, reason: 'Missing required module' };
    }
    
    return { canPurchase: true, module };
  }, [hasModule]);

  // Purchase module
  const purchaseModule = useCallback((moduleId) => {
    const check = canPurchaseModule(moduleId);
    if (!check.canPurchase) {
      return { success: false, message: check.reason };
    }
    
    setInstalledModules(prev => [...prev, moduleId]);
    
    return { success: true, module: check.module };
  }, [canPurchaseModule]);

  // Get cargo capacity from modules
  const getCargoCapacity = useCallback(() => {
    let capacity = 0;
    
    // Base cargo bay
    if (hasModule('cargoBay')) {
      capacity = shipModules.cargoBay.effects.cargoCapacity || 20;
    } else {
    }
    
    // Expanded cargo bay multiplier (only applies if base cargo bay exists)
    if (hasModule('expandedCargoBay') && capacity > 0) {
      const multiplier = shipModules.expandedCargoBay.effects.cargoCapacityMultiplier || 2;
      capacity *= multiplier;
    }
    
    return capacity;
  }, [hasModule]);

  // Get all active effects
  const getActiveEffects = useCallback(() => {
    const effects = {};
    
    installedModules.forEach(moduleId => {
      const module = shipModules[moduleId];
      if (module && module.effects) {
        Object.entries(module.effects).forEach(([key, value]) => {
          if (key.endsWith('Multiplier')) {
            // Multiply existing value
            const baseKey = key.replace('Multiplier', '');
            effects[baseKey] = (effects[baseKey] || 1) * value;
          } else {
            // Add or set value
            effects[key] = value;
          }
        });
      }
    });
    
    return effects;
  }, [installedModules]);

  // Get modules available at location
  const getAvailableModules = useCallback((locationType) => {
    return Object.values(shipModules).filter(module => 
      module.availableAt.includes(locationType) && !hasModule(module.id)
    );
  }, [hasModule]);

  // Reset (for game reset)
  const resetModules = useCallback(() => {
    setInstalledModules([]);
    localStorage.removeItem(STORAGE_KEYS.INSTALLED_MODULES);
  }, []);

  const value = {
    installedModules,
    hasModule,
    canPurchaseModule,
    purchaseModule,
    getCargoCapacity,
    getActiveEffects,
    getAvailableModules,
    resetModules
  };

  return (
    <ModulesContext.Provider value={value}>
      {children}
    </ModulesContext.Provider>
  );
}

export function useModules() {
  const context = useContext(ModulesContext);
  if (!context) {
    throw new Error('useModules must be used within a ModulesProvider');
  }
  return context;
}

export default ModulesContext;
